import { useContext, useEffect, useState } from 'react';
import * as R from 'ramda';

import WorkTemplatesContext from '@atom/components/workTemplates/WorkTemplatesContext';
import FolderOptions from '@atom/components/workTemplates/workTemplatesFolderTree/FolderOptions';
import { Icon, ListTable, Progress } from '@atom/mui';
import { WorkTemplateFolderTree } from '@atom/types/workTemplate';
import api from '@atom/utilities/api';
import { WORK_TEMPLATES_FOLDER_ENDPOINT } from '@atom/utilities/endpoints';
import { setDisplayDate } from '@atom/utilities/timeUtilities';

const { TableHead, TableRow, TableCell, TableBody } = ListTable;

const styles = {
  progress: {
    height: '100%',
  },
  name: {
    cursor: 'pointer',
    fontWeight: 500,
    overflow: 'visible',
    whiteSpace: 'nowrap',
  },
};

export interface Props {
  workTemplateFolderTree: WorkTemplateFolderTree;
}

const WorkTemplatesFolderList = ({ workTemplateFolderTree }: Props) => {
  const {
    activeFolderId,
    setActiveFolderId,
    open,
    setWorkTemplatesView,
    toggleOpen,
    setIsActiveFolderLeafNode,
  } = useContext(WorkTemplatesContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [workTemplateFolders, setWorkTemplateFolders] = useState<any[]>([]);

  useEffect(() => {
    const getWorkTemplates = async () => {
      setLoading(true);

      const { data } = await api.get(WORK_TEMPLATES_FOLDER_ENDPOINT, {
        parentFolderId: activeFolderId,
      });

      setWorkTemplateFolders(data);

      setLoading(false);
    };

    getWorkTemplates();
  }, [activeFolderId, workTemplateFolderTree]);

  return (
    <>
      {loading ? (
        <Progress style={styles.progress} />
      ) : (
        <ListTable offsetTop="110px">
          <TableHead>
            <TableRow header>
              <TableCell variant="head" />
              <TableCell variant="head">Name</TableCell>
              <TableCell variant="head">Created By</TableCell>
              <TableCell variant="head">Created On</TableCell>
              <TableCell variant="head" />
            </TableRow>
          </TableHead>
          <TableBody>
            {workTemplateFolders.map(folder => {
              const onClick = () => {
                if (!open.has(activeFolderId)) {
                  toggleOpen(activeFolderId);
                }
                setActiveFolderId(folder.id);
                // The sidebar row's useEffect ordinarily flips this flag
                // when activeFolderId matches its folder.id, but it only
                // fires once the sidebar is expanded and the row mounts.
                // Set it here so the right pane swaps to WorkTemplatesList
                // immediately on click, regardless of sidebar state.
                setIsActiveFolderLeafNode(
                  R.isEmpty((folder as any).children ?? []),
                );
                setWorkTemplatesView({ page: 1 });
              };
              return (
                <TableRow key={folder.id}>
                  <TableCell width="2rem" align="right">
                    <Icon>folder</Icon>
                  </TableCell>
                  <TableCell>
                    <div style={styles.name} onClick={onClick}>
                      {folder.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    {!R.isNil(folder.createdBy)
                      ? `${folder.createdBy.firstName} ${folder.createdBy.lastName}`
                      : '-'}
                  </TableCell>
                  <TableCell>{setDisplayDate(folder.createdDate)}</TableCell>
                  <TableCell width="3rem">
                    <FolderOptions folder={folder} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </ListTable>
      )}
    </>
  );
};

export default WorkTemplatesFolderList;
